/* eslint-disable @typescript-eslint/no-use-before-define */
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, SetStateAction } from 'react';

import { DATA_SPLITTER_ATTRIBUTE, LeftPanelTypes, RightPanelElems, TreatmentBuilderContext } from '@routes';
import { func } from '@utils';

import {
  DATA_FILLED_ATTRIBUTE,
  // DOC_REDEFINE_DELAY,
  SPLITTER_LOADER_STYLES_ID,
} from '../SampleTemplate.const';
import {
  // CurrentTarget,
  HTMLElementWithDocument,
  WrapperTagNames,
} from '../SampleTemplate.types';
import { getUniqueStyles, resetPrevElem, removeElemInfo } from '../SampleTemplate.utils';

import {
  // HighlightedElement,
  // IEHTMLTextAreaElement,
  UseWrappModalHtmlElemsHook,
} from './ModuleModal.types';
import {
  attachIframeOverlays,
  Cursor,
  elemClick,
  elemMouseEnter,
  elemMouseLeave,
  // getHighlightedElems,
  initializeElemIds,
  initializeLoadingImages,
  initializeLinkIds,
  initializeDivIds,
  pasteTextIntoTextarea,
  positionIframeOverlay,
  resizeModalIframe,
  setCaretPosition,
  // addWidths,
  removeIframeOverlays,
} from './ModuleModal.utils';
import {
  // DEFAULT_HIGHLIGHTED_ELEMS,
  EDITABLE_ELEMENT_TAG_NAMES,
  // HIGHLIGHTED_ELEM_BORDER_WIDTH,
  MODULE_CONTENT_DIV_ID,
  MODULE_DIV_CONTAINER_ID,
  MODULE_ELEMENT_ID_PREFIX,
  // MODULE_INTERACTION_ACTIVE_HEIGHT,
  MODULE_INTERACTION_ACTIVE_ID,
  MODULE_INTERACTION_ACTIVE_ATTRIBUTE,
  MODULE_INTERACTION_DRAG_ATTRIBUTE,
  MODULE_INTERACTION_HOVER_ATTRIBUTE,
  MODULE_INTERACTION_IFRAME_BODY_ID,
  MODULE_INTERACTION_STYLES_ID,
  MODULE_INTERACTION_STYLES,
  ORIGINAL_ID_ATTRIBUTE,
} from './ModuleModal.const';
import { ModuleModalClearModuleHtmlGetter, ModuleModalProps } from './ModuleModal.props';
import { ViewportSize, ViewportSizeContext, ViewportWidth } from '@layouts';
// import { delay } from '@utils/func/func';
import { variables } from '@styles';
// import { createImportSpecifier } from 'typescript';
import { PERSONALIZATION_TOKEN_TAG } from '@utils/personalization';

/**
 * ModuleModal hooks
 * @returns {Function}
 */

export const useWrappModalHtmlElems = (
  originalInnerHtml: string | undefined,
  isEditorChanged: boolean,
  documentElemId: string,
  templateStyles: HTMLCollectionOf<HTMLStyleElement> | null | undefined,
  templateIframeId: string,
  setRightPanelElem: (value: SetStateAction<RightPanelElems>) => void,
  onChange: ModuleModalProps['onChange'],
  isTemplateEditor: boolean,
  handleInputBlur?: ModuleModalProps['onInputBlur'],
): UseWrappModalHtmlElemsHook => {
  const [redefinedDoc, setRedefinedDoc] = useState('');
  // const [highlightedElems, setHighlightedElems] = useState<HighlightedElement[]>(DEFAULT_HIGHLIGHTED_ELEMS);
  const [currentAnchorOffset, setCurrentAnchorOffset] = useState<number | null>(null);
  const [iframeHtmlRedefined, setIframeHtmlRedefined] = useState(false);

  const wrapperRef = useRef(0);

  const {
    chosenElemId,
    chosenModuleElemId,
    selectedActiveModule,
    // cursorPosition,
    positionSelectedModuleIframeOverlay,
    setCursorPosition,
    setChosenModuleElemId,
    setChosenElemStyles,
    setWysiwygContainerType,
    setIsModuleChanged,
    setElemInputActive,
    setLeftPanelType,
  } = useContext(TreatmentBuilderContext);

  const { viewportSize } = useContext(ViewportSizeContext);

  // create a div container for the module modal html and inserts the passed html
  const redefineHtml = useCallback(
    (initialHtml: string | undefined) => {
      // Check if innerHtml is defined
      if (initialHtml) {
        // Get the contentDocument of the element with the specified documentElemId
        const iframe = document.getElementById(documentElemId) as HTMLIFrameElement;
        const contentDocument = iframe.contentDocument;

        if (contentDocument && contentDocument.body) {
          const existingDivContainer = contentDocument.getElementById(MODULE_DIV_CONTAINER_ID);
          let divContainer: HTMLElement | null = null;

          if (existingDivContainer) {
            divContainer = existingDivContainer;
          } else {
            // if there is no existing div container, create a new one
            divContainer = document.createElement('div');
            divContainer.id = MODULE_DIV_CONTAINER_ID;

            if (contentDocument.body.innerHTML !== '') {
              // set the innerHTML of the div container to the contentDocument's body innerHTML
              divContainer.innerHTML = contentDocument.body.innerHTML;
            }
          }

          // get the existing innerHtml of the div container for comparison
          const divContainerInitialHtml = divContainer.innerHTML;

          // Create a new DOMParser instance
          const parser = new DOMParser();

          // parse the innerHtml string into a document
          // const parsedDoc = parser.parseFromString(`<div class="initial-wrapper">${innerHtml}</div>`, 'text/html');
          const parsedDoc = parser.parseFromString(`${initialHtml}`, 'text/html');

          // check for data-sample-template-journey attribute within passed initialHtml
          const dataFilledAttributeDiv = parsedDoc.querySelector(`[${DATA_FILLED_ATTRIBUTE}]`);
          const parsedHtml = dataFilledAttributeDiv?.innerHTML ?? parsedDoc.body.innerHTML;

          // if there is a difference between the parsed document and the div container's initial innerHTML
          // replace the div container's innerHTML with the parsed document's innerHTML
          if (parsedDoc.body && parsedHtml !== divContainerInitialHtml) {
            // set the innerHTML of the div container to the parsed innerHTML
            divContainer.innerHTML = parsedHtml;

            // divContainer.innerHTML = parsedDoc.documentElement.getHTML();

            // const childNodes = parsedDoc.body.childNodes;
            // for (let i = 0; i < childNodes.length; i++) {
            //   divContainer.appendChild(childNodes[i]);
            // }

            // append the divContainer to the iframe body
            if (!existingDivContainer) {
              contentDocument.body.innerHTML = divContainer.innerHTML;
            }

            setIframeHtmlRedefined(true);

            return divContainer.innerHTML;
          } else {
            // append the divContainer to the iframe body
            if (!existingDivContainer) {
              contentDocument.body.innerHTML = divContainer.innerHTML;
            }

            setIframeHtmlRedefined(true);

            return divContainer.innerHTML;
          }
        }
      }

      // Return an empty string if initialHtml is not defined or if contentDocument's body is not found
      return '';
    },
    [documentElemId],
  );

  const handleElemMouseEnter = useCallback(
    (e: MouseEvent, elemId: string) => {
      // get the iframe and all elements with a hovered attribute
      const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
      const hoveredElems = contentDocument.querySelectorAll(`[${MODULE_INTERACTION_HOVER_ATTRIBUTE}="true"]`);

      // remove the hover attribute from all elements except the one being hovered
      hoveredElems.forEach((element: Element) => {
        if (element.id !== elemId) {
          element.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
        }
      });

      elemMouseEnter(e, elemId, documentElemId, false);
    },
    [documentElemId],
  );

  const handleElemMouseLeave = useCallback(
    (e: MouseEvent, elemId: string) => {
      if (chosenModuleElemId !== elemId) {
        elemMouseLeave(e);
      }
    },
    [chosenModuleElemId],
  );

  const handleElemClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      let id = (e.target as HTMLElement).id;
      const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
      let element = contentDocument.getElementById(id);

      if ((e.target as HTMLElement)?.tagName === 'TD') {
        element = e.target as HTMLElement;
        do {
          element = element?.parentElement ?? null;
        } while (!['DIV', 'BODY'].includes(element?.tagName ?? ''));

        id = element?.id ?? '';
        element?.setAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE, 'true');
      }

      // const selectedElems = contentDocument.querySelectorAll(
      //   `[style*="border-color: ${variables.colorV2.module.border.active}]`,
      // );
      // const selectedBorderElems = contentDocument.querySelectorAll(
      //   `[style*="border: .2rem solid ${variables.colorV2.module.border.active}"]`,
      // );

      // const elems: any = Array.from(selectedElems)
      //   .concat(Array.from(selectedBorderElems))
      //   .filter((elem: any) => elem.id !== id && !elem.id.includes('journey'));
      // const prevElemId = elems[0]?.id ?? highlightedElems[1]?.id ?? '';

      // const target = e.currentTarget as any;
      // addElemInfo(target, id, documentElemId);

      if (element?.getAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE) === 'true' && e) {
        const styles = element?.getAttribute('style');
        const elemStyles = getUniqueStyles(styles ?? '');
        const selection = contentDocument.getSelection();

        setChosenElemStyles(elemStyles);
        elemClick(e, id, documentElemId); // setChosenModuleElemId);

        setChosenModuleElemId(id);

        setCurrentAnchorOffset(selection?.anchorOffset !== undefined ? selection?.anchorOffset : null);

        // if (id.includes(`${MODULE_ELEMENT_ID_PREFIX}button`)) {
        if (
          id.includes(`${MODULE_ELEMENT_ID_PREFIX}a`) &&
          (e.currentTarget as HTMLElement)?.hasAttribute('data-element-type') &&
          (e.currentTarget as HTMLElement).getAttribute('data-element-type') === 'button'
        ) {
          setRightPanelElem(RightPanelElems.BUTTON_SETTINGS);
          setWysiwygContainerType('button');
          return;
        }

        if (id.includes(`${MODULE_ELEMENT_ID_PREFIX}a`)) {
          setRightPanelElem(RightPanelElems.WYSIWYG_UNIVERSAL);
          setWysiwygContainerType('text');
        }

        if (
          ['p-', 'h1-', 'h2-', 'h3-', 'h4-', 'h5-', 'span-', 'strong-'].some((el) =>
            id.includes(MODULE_ELEMENT_ID_PREFIX + el),
          )
        ) {
          setRightPanelElem(RightPanelElems.WYSIWYG_UNIVERSAL);
          setWysiwygContainerType('text');
        }
        if (
          id.includes(`${MODULE_ELEMENT_ID_PREFIX}img-`) &&
          element?.getAttribute('data-visualization-station') === 'true'
        ) {
          setRightPanelElem(RightPanelElems.WYSIWYG_DATA_VISUAL);
          setLeftPanelType(LeftPanelTypes.DATA_VISUALIZATIONS);
        } else if (id.includes(`${MODULE_ELEMENT_ID_PREFIX}img-`)) {
          setRightPanelElem(RightPanelElems.WYSIWYG_IMAGE);
          setLeftPanelType(LeftPanelTypes.IMAGES);
        }
        if (id.includes(`${MODULE_ELEMENT_ID_PREFIX}div-`)) {
          setRightPanelElem(RightPanelElems.WYSIWYG_DIV);
        }
      }
    },
    [
      documentElemId,
      // highlightedElems,
      setChosenElemStyles,
      setChosenModuleElemId,
      setLeftPanelType,
      setRightPanelElem,
      setWysiwygContainerType,
    ],
  );

  const redefineDivElemMethods = useCallback(
    (contentDocument: Document) => {
      const divs = Array.from(contentDocument.querySelectorAll('div'));

      divs
        .filter((item) => item.id.includes(`${MODULE_ELEMENT_ID_PREFIX}div`))
        .forEach((elem) => {
          elem.onmouseover = (e) => handleElemMouseEnter(e, elem.id);
          elem.onmouseout = (e) => handleElemMouseLeave(e, elem.id);
          elem.onclick = (e) => handleElemClick(e);
        });
    },
    [handleElemClick, handleElemMouseEnter, handleElemMouseLeave],
  );

  // const handleElemMouseMove = useCallback(
  //   (e: MouseEvent, elemId: string) => {
  //     if ((e.target as CurrentTarget).id === elemId) {
  //       // handleElemMouseEnter(e, elemId);
  //     }
  //   },
  //   [handleElemMouseEnter],
  // );

  const getClearModuleHtml = useCallback<ModuleModalClearModuleHtmlGetter>(() => {
    // reset element inline styles (which is no longer necessary)
    // and attempt to remove elem-info and trash
    // (nothing appears to match when editing module)
    resetPrevElem('', documentElemId, chosenModuleElemId, func.nop); // clear selection
    removeElemInfo('', documentElemId); // clear hover

    const moduleFrame = document.getElementById(documentElemId) as HTMLIFrameElement | null;
    const innerDoc = moduleFrame?.contentDocument || moduleFrame?.contentWindow?.document;
    const moduleElement = innerDoc?.querySelector(`[${DATA_SPLITTER_ATTRIBUTE}]`);

    return {
      moduleHtml: moduleElement?.innerHTML || innerDoc?.body?.innerHTML || '',
      filledAttrValue: moduleElement?.getAttribute(DATA_FILLED_ATTRIBUTE) || '',
    };
  }, [documentElemId, chosenModuleElemId]);

  const handleElemDoubleCLick = useCallback(
    (e: MouseEvent, elemId: string, contentDocument: Document) => {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      const textElem = contentDocument.getElementById(elemId);

      // WeakSet to avoid blurring the same element twice within the same timeframe
      const blurredElements = new WeakSet<Element>();

      const handleBlur = async (event: FocusEvent) => {
        const elem = event.target as Element;

        // Check if the element is already being blurred
        if (blurredElements.has(elem)) return;
        blurredElements.add(elem);

        const textAreaElemTarget = event.target as Element; //HTMLElement;

        if (checkIfSignalBtnClicked(textAreaElemTarget)) {
          setTimeout(() => blurredElements.delete(elem), 100);
          return;
        }

        const parser = new DOMParser();
        const originalElem = textAreaElemTarget.getAttribute('original-element');
        const parsedOriginalElem = parser.parseFromString(String(originalElem), 'text/html').getElementById(elemId);
        const elemInfos = Array.from(contentDocument.querySelectorAll(`div`)).filter((item) =>
          item.id.includes('elem-info'),
        );

        const currentElemInfo = elemInfos.find((item) => item.id === `elem-info-${chosenModuleElemId}`);

        if (currentElemInfo) {
          parsedOriginalElem?.setAttribute('elem-info', currentElemInfo.outerHTML);
        }

        if (parsedOriginalElem) {
          elemInfos.forEach((elemInfo) => elemInfo.remove());
          parsedOriginalElem.innerHTML =
            textAreaElemTarget?.innerHTML.replaceAll('color: transparent;', 'color: #000;') ?? '';
          parsedOriginalElem.setAttribute('originalinnerhtml', textAreaElemTarget.innerHTML);
          textAreaElemTarget.replaceWith(parsedOriginalElem);
          // parsedOriginalElem.onmouseover = (ev) => handleElemMouseMove(ev, elemId);
          parsedOriginalElem.onmouseover = (ev) => handleElemMouseEnter(ev, elemId);
          parsedOriginalElem.onmouseout = (ev) => handleElemMouseLeave(ev, elemId);
          parsedOriginalElem.ondblclick = (ev) => handleElemDoubleCLick(ev, elemId, contentDocument);
          parsedOriginalElem.onclick = (ev) => handleElemClick(ev);
          parsedOriginalElem.style.whiteSpace = 'normal';
          // parsedOriginalElem.style.borderColor = 'transparent';
          parsedOriginalElem.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);

          const tokens = parsedOriginalElem.querySelectorAll(PERSONALIZATION_TOKEN_TAG);
          tokens.forEach((token) => {
            token.removeAttribute('initialvalue');
          });

          setElemInputActive('completed');

          // Remove from the set after a delay to allow future blurs:
          setTimeout(() => blurredElements.delete(elem), 100);
        }

        onChange(contentDocument.body.innerHTML, initRedefinedDocRef.current !== redefinedDoc, getClearModuleHtml);
        if (parsedOriginalElem) {
          handleInputBlur?.(parsedOriginalElem, getClearModuleHtml);
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          const input = event.target as HTMLInputElement;

          if (event.shiftKey && event.type == 'keydown') {
            pasteTextIntoTextarea(input, '\n');
          }

          setIsModuleChanged(true);
          input.blur();
        }

        // if (event.key === 'Escape') {
        //   const focusEvent = new FocusEvent('blur', { relatedTarget: event.target });
        //   handleBlur(focusEvent);
        // }
      };

      // update the modal iframe height and the active overlay position on update received
      const updateModalAndActiveOverlay = (event: Event) => {
        setCursorPosition(Cursor.getCurrentCursorPosition(event.target as HTMLElement));

        const iframe = document.getElementById(documentElemId) as HTMLIFrameElement;
        const iframeBody = iframe.contentDocument?.body;

        if (iframeBody) {
          // update the iframe height based on the content
          resizeModalIframe(iframe, isTemplateEditor);

          // adjust the position of the active overlay
          const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
          const targetElem = event.target as HTMLElement;
          if (activeOverlay && targetElem) {
            positionIframeOverlay(activeOverlay, targetElem);
          }
        }

        if (positionSelectedModuleIframeOverlay) {
          positionSelectedModuleIframeOverlay(selectedActiveModule);
        }
      };

      const handleKeyUp = (event: KeyboardEvent) => {
        updateModalAndActiveOverlay(event);
      };

      if (textElem) {
        const textArea = contentDocument.createElement('pre');
        const textElemStyles = textElem?.getAttribute('style')?.split(';').excludeStrs(['cursor']).join(';\n');
        textArea.innerHTML = textElem.innerHTML.replaceAll(/\n/gm, '').replace(/\s+/g, ' ') || '';

        // textArea.setAttribute(
        //   'style',
        //   getUniqueStyles(`
        //   ${textElemStyles}
        //   outline: 1px solid black;
        //   line-height: normal;
        //   overflow: auto;
        //   resize: none;
        //   z-index: 100;
        //   white-space: pre-wrap;
        // `),
        // );
        textArea.setAttribute('style', getUniqueStyles(textElemStyles ?? ''));

        setCursorPosition(0);
        setElemInputActive('enabled');
        setChosenModuleElemId(elemId);

        const parentElement = textElem.parentElement;

        let fontSize = textElem.style.fontSize;
        let fontFamily = textElem.style.fontFamily;
        let fontWeight = textElem.style.fontWeight;
        let color = textElem.style.color;
        let lineHeight = textElem.style.lineHeight;
        let textDecoration = textElem.style.textDecoration;
        let textTransform = textElem.style.textTransform;

        // get parent element styles if set and element has no value
        if (parentElement) {
          fontSize = fontSize || parentElement.style.fontSize;
          fontFamily = fontFamily || parentElement.style.fontFamily;
          fontWeight = fontWeight || parentElement.style.fontWeight;
          color = color || parentElement.style.color;
          lineHeight = lineHeight || parentElement.style.lineHeight;
          textDecoration = textDecoration || parentElement.style.textDecoration;
          textTransform = textTransform || parentElement.style.textTransform;
        }

        // set textArea styles
        // get textElem computed margin and padding styles
        const computedStyles = window.getComputedStyle(textElem);
        const marginTop = computedStyles.marginTop;
        const marginRight = computedStyles.marginRight;
        const marginBottom = computedStyles.marginBottom;
        const marginLeft = computedStyles.marginLeft;
        const margin = `${marginTop} ${marginRight} ${marginBottom} ${marginLeft}`;
        textArea.style.margin = margin;

        const paddingTop = computedStyles.paddingTop;
        const paddingRight = computedStyles.paddingRight;
        const paddingBottom = computedStyles.paddingBottom;
        const paddingLeft = computedStyles.paddingLeft;
        const padding = `${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}`;
        textArea.style.padding = padding;

        textArea.style.display = computedStyles.display;
        textArea.style.whiteSpace = 'normal';

        textArea.setAttribute('original-element', textElem.outerHTML);
        textArea.setAttribute('id', elemId);
        textArea.setAttribute('contenteditable', 'true');
        // textArea.style.background = 'transparent';
        textArea.style.fontSize = fontSize || 'inherit';
        textArea.style.fontFamily = fontFamily || 'inherit';
        textArea.style.fontWeight = fontWeight || 'inherit';
        textArea.style.color = color || 'inherit';
        textArea.style.lineHeight = lineHeight || 'inherit';
        textArea.style.textDecoration = textDecoration || 'inherit';
        textArea.style.textTransform = textTransform || 'inherit';
        textArea.onblur = (event) => handleBlur(event);
        textArea.onkeydown = (event) => handleKeyDown(event);
        textArea.onkeyup = (event) => handleKeyUp(event);
        textArea.oninput = (event) => {
          setIsModuleChanged(true);
          // setCursorPosition(Cursor.getCurrentCursorPosition(event.target as HTMLElement));
          updateModalAndActiveOverlay(event);
        };
        textArea.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          setCursorPosition(Cursor.getCurrentCursorPosition(event.target as HTMLElement));
        };

        // highlightsContainer.appendChild(textArea);

        textElem.replaceWith(textArea);

        const tokens = textArea.querySelectorAll(PERSONALIZATION_TOKEN_TAG);
        Array.from(tokens).forEach((token: any) => {
          token.setAttribute('initialvalue', token.innerText);
          token.addEventListener(
            'DOMSubtreeModified',
            (event: any) => {
              if (event.target.parentElement && event.target.parentElement.getAttribute('initialValue')) {
                event.target.parentElement.innerHTML = event.target.parentElement.getAttribute('initialValue');
              }
            },
            false,
          );
        });
        setCaretPosition(textArea as any, currentAnchorOffset);
        textArea.focus();
      }
    },
    [
      chosenModuleElemId,
      currentAnchorOffset,
      documentElemId,
      getClearModuleHtml,
      handleElemClick,
      handleElemMouseEnter,
      handleElemMouseLeave,
      // handleElemMouseMove,
      handleInputBlur,
      onChange,
      positionSelectedModuleIframeOverlay,
      redefinedDoc,
      selectedActiveModule,
      setChosenModuleElemId,
      setCursorPosition,
      setElemInputActive,
      setIsModuleChanged,
    ],
  );

  const handleSelectionChange = useCallback((e: Event, contentDocument: Document) => {
    return { e, contentDocument };
    //TODO: finished logic for handleSelectionChange;
    // const selection = contentDocument.getSelection();
    // const anchorElemId = selection?.anchorNode?.parentElement?.id;
    // const focusElemId = selection?.focusNode?.parentElement?.id;
    // const selectedElem = contentDocument.getElementById(String(anchorElemId));
    // const { anchorOffset, focusOffset } = selection as Selection;
    // if (selectedElem) {
    //   const trimmedElemInnerHTML = selectedElem?.innerHTML.replaceAll('\n', '').trim();
    //   const selectedText = trimmedElemInnerHTML.slice(anchorOffset, focusOffset);
    //   const textBefore = trimmedElemInnerHTML.slice(0, anchorOffset);
    //   const textAfter = trimmedElemInnerHTML.slice(
    //     focusOffset,
    //     (selection?.anchorNode as unknown as string).length,
    //   );
    //   selectedElem.innerHTML = `${textBefore}<span>${selectedText}</span>${textAfter}`;
    // }
    // if (selectedElem) {
    //   const range = new Range();
    //   range.setStart(contentDocument, anchorOffset);
    //   range.setEnd(contentDocument, focusOffset);
    // }
  }, []);

  const redefineLinkElemMethods = useCallback(
    (contentDocument: Document) => {
      const links = contentDocument.querySelectorAll('a');
      Array.from(links)
        .filter((item) => item.id.includes(`${MODULE_ELEMENT_ID_PREFIX}a`))
        .forEach((elem) => {
          elem.onmouseover = (e) => handleElemMouseEnter(e, elem.id);
          elem.onmouseout = (e) => handleElemMouseLeave(e, elem.id);
          elem.onclick = (e) => handleElemClick(e);

          if (elem.getAttribute('data-element-type') === 'button') {
            elem.ondblclick = (e) => handleElemDoubleCLick(e, elem.id, contentDocument);
            contentDocument.onselectionchange = (e) => handleSelectionChange(e, contentDocument);
          }
        });

      const buttons = contentDocument.querySelectorAll('button');
      Array.from(buttons)
        .filter((item) => item.id.includes(`${MODULE_ELEMENT_ID_PREFIX}button`))
        .forEach((elem) => {
          elem.onmouseover = (e) => handleElemMouseEnter(e, elem.id);
          elem.onmouseout = (e) => handleElemMouseLeave(e, elem.id);
          elem.onclick = (e) => handleElemClick(e);
        });
    },
    // [handleElemClick, handleElemMouseEnter, handleElemMouseLeave],
    [handleElemClick, handleElemDoubleCLick, handleElemMouseEnter, handleElemMouseLeave, handleSelectionChange],
  );

  const redefineElemMethods = useCallback(
    (contentDocument: Document, tagName: WrapperTagNames) => {
      const elems = contentDocument.querySelectorAll(tagName);
      const tags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'span', 'p'];

      Array.from(elems)
        .filter((item) => item.id.includes(MODULE_ELEMENT_ID_PREFIX + tagName))
        .forEach((elem) => {
          elem.onmouseover = (e) => handleElemMouseEnter(e, elem.id);
          elem.onmouseout = (e) => handleElemMouseLeave(e, elem.id);
          elem.onclick = (e) => handleElemClick(e);

          if (tags.includes(tagName)) {
            elem.ondblclick = (e) => handleElemDoubleCLick(e, elem.id, contentDocument);
            contentDocument.onselectionchange = (e) => handleSelectionChange(e, contentDocument);

            const elemInfo = elem.getAttribute('elem-info');

            if (elemInfo) {
              const parser = new DOMParser();
              const parsedElemInfo = parser.parseFromString(elemInfo, 'text/html').getElementsByTagName('div')[0];

              elem.after(parsedElemInfo);
              elem.removeAttribute('elem-info');
            }
          }
        });
    },
    [handleElemClick, handleElemDoubleCLick, handleElemMouseEnter, handleElemMouseLeave, handleSelectionChange],
  );

  const redefineElements = useCallback(() => {
    const iframe = document.getElementById(documentElemId) as HTMLElementWithDocument;
    const contentDocument = iframe.contentDocument;

    // const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
    initializeElemIds(contentDocument, 'img');
    initializeLoadingImages(iframe as HTMLIFrameElement, contentDocument, isTemplateEditor);
    initializeElemIds(contentDocument, 'h1');
    initializeElemIds(contentDocument, 'h2');
    initializeElemIds(contentDocument, 'h3');
    initializeElemIds(contentDocument, 'h4');
    initializeElemIds(contentDocument, 'h5');
    initializeElemIds(contentDocument, 'h6');
    initializeElemIds(contentDocument, 'strong');
    initializeElemIds(contentDocument, 'span');
    initializeElemIds(contentDocument, 'p');
    // initializeElemIds(contentDocument, 'button');
    initializeLinkIds(contentDocument);
    initializeDivIds(contentDocument);

    if (iframeHtmlRedefined) {
      // Redefine methods for various elements within the iframe's content document
      redefineElemMethods(contentDocument, 'h1');
      redefineElemMethods(contentDocument, 'h2');
      redefineElemMethods(contentDocument, 'h3');
      redefineElemMethods(contentDocument, 'h4');
      redefineElemMethods(contentDocument, 'h5');
      redefineElemMethods(contentDocument, 'h6');
      redefineElemMethods(contentDocument, 'img');
      redefineElemMethods(contentDocument, 'strong');
      redefineElemMethods(contentDocument, 'span');
      redefineElemMethods(contentDocument, 'p');
      redefineLinkElemMethods(contentDocument);
      redefineDivElemMethods(contentDocument);
    }

    // Set the iframe height to match the height of its body content
    if (contentDocument.body) {
      resizeModalIframe(iframe as HTMLIFrameElement, isTemplateEditor);
      // const iframeBody = contentDocument.body;
      // const iframeHeight = iframeBody.getBoundingClientRect().height;

      // iframe.style.height = `${iframeHeight + MODULE_INTERACTION_ACTIVE_HEIGHT}px`;
    }

    // if (iframe.style.height !== `${iframe.contentDocument.body.getBoundingClientRect().height}px`) {
    //   iframe.style.height = `${iframe.contentDocument.body.getBoundingClientRect().height}px`;
    // }
  }, [documentElemId, iframeHtmlRedefined, redefineElemMethods, redefineLinkElemMethods, redefineDivElemMethods]);

  // useEffect(() => {
  //   const prevHighlightedElem = highlightedElems[1];

  //   if (prevHighlightedElem?.event) {
  //     handleElemMouseLeave(prevHighlightedElem.event, prevHighlightedElem.id);
  //   }
  // }, [handleElemMouseLeave, highlightedElems]);

  const checkIfSignalBtnClicked = (textAreaElemTarget: any) => {
    const addSignalBtn = document.getElementById('add-signal-btn');
    const signalItemsCollection = document.getElementsByClassName('signal-item');

    if (addSignalBtn) {
      const rects = addSignalBtn.getBoundingClientRect();
      const mouseX = (window as any).currentMouseX;
      const mouseY = (window as any).currentMouseY;
      const signalItems = Array.from(signalItemsCollection);
      const condition = signalItems.reduce((acc, item) => {
        const itemRects = item.getBoundingClientRect();

        return (
          acc ||
          (mouseX >= itemRects.left &&
            mouseY <= itemRects.right &&
            mouseY >= itemRects.top &&
            mouseY <= itemRects.bottom)
        );
      }, false);

      if (
        condition ||
        (mouseX >= rects.left && mouseY <= rects.right && mouseY >= rects.top && mouseY <= rects.bottom)
      ) {
        textAreaElemTarget.focus();

        return true;
      }
    }

    return false;
  };

  // useEffect hook to monitor changes in originalInnerHtml, redefinedDoc, isEditorChanged, and redefineHtml
  useEffect(() => {
    if (originalInnerHtml === undefined || originalInnerHtml === null) {
      if (!redefinedDoc) {
        // Update the redefinedDoc state with the new redefined HTML
        setRedefinedDoc('');
      }

      return;
    }

    // parse originalInnerHtml as HTML
    const parser = new DOMParser();

    // create a new document from the originalInnerHtml
    const doc = parser.parseFromString(originalInnerHtml, 'text/html');

    // find all the anchor tags and give them a temphref attribute if they have an href attribute and
    // they do not already have temphref
    const anchors = doc.querySelectorAll('a[href]:not([temphref])');
    anchors.forEach((anchor) => {
      // set the temphref attribute to the href attribute value
      anchor.setAttribute('temphref', anchor.getAttribute('href') || '');
    });

    // Redefine the HTML by replacing certain characters and attributes
    // const redefinedHtml = redefineHtml(originalInnerHtml).replaceAll('&amp;', `&`).replaceAll('href', 'temphref');

    const redefinedHtml = redefineHtml(doc.documentElement.outerHTML).replaceAll('&amp;', `&`);

    // Check if redefinedDoc is not set or if it has changed and the editor has been modified
    if (!redefinedDoc || (redefinedDoc !== redefinedHtml && isEditorChanged)) {
      // Update the redefinedDoc state with the new redefined HTML
      setRedefinedDoc(redefinedHtml);
    }
  }, [originalInnerHtml, redefinedDoc, isEditorChanged, redefineHtml]);

  useEffect(() => {
    if (iframeHtmlRedefined) {
      const iframe = document.getElementById(documentElemId) as HTMLIFrameElement;
      attachIframeOverlays(iframe);
      redefineElements();
      setIframeHtmlRedefined(false);
    }
  }, [documentElemId, iframeHtmlRedefined, redefineElements]);

  const onIframeLoad = useCallback(
    (cb: () => void, elemId: string) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;

      iframe.onload = async () => {
        // reset the left library panel to images view
        setLeftPanelType(LeftPanelTypes.IMAGES);

        const blockGrid = iframe.contentDocument.querySelector('.block-grid') as HTMLElement;
        const iframeBody = iframe.contentDocument.body;
        iframeBody.id = MODULE_INTERACTION_IFRAME_BODY_ID;

        const iframeHead = iframe.contentDocument.head;

        // Attach the SampleTemplate styles and iframe overlay styles
        if (templateStyles && iframeHead) {
          // clone and append nodes to maintain the styles in the iframe fromt the parent iframe
          Array.from(templateStyles).forEach((style: HTMLStyleElement) => {
            if (style.id !== SPLITTER_LOADER_STYLES_ID && style.id !== MODULE_INTERACTION_STYLES_ID) {
              iframeHead.appendChild(style.cloneNode(true));
            }
          });
        }

        // styles for hover and active states
        const moduleInteractionStyles = iframe.contentDocument.createElement('style');
        moduleInteractionStyles.setAttribute('type', 'text/css');
        moduleInteractionStyles.id = MODULE_INTERACTION_STYLES_ID;
        moduleInteractionStyles.innerHTML = MODULE_INTERACTION_STYLES;

        iframeHead.appendChild(moduleInteractionStyles);

        resizeModalIframe(iframe as HTMLIFrameElement, isTemplateEditor);

        if (blockGrid) {
          blockGrid.style.display = 'block';
          blockGrid.style.overflow = 'inherit';
          blockGrid.removeAttribute('transformed');

          if (iframe.parentElement && blockGrid.parentElement) {
            blockGrid.parentElement.style.overflow = 'hidden';
          }

          const blockGrid1 = iframe.contentDocument.querySelector('.block-grid') as HTMLElement;

          if (blockGrid1) {
            blockGrid1.style.maxWidth = `${blockGrid1.getBoundingClientRect().width + 50}px`;
          }
        }

        if (iframeBody) {
          // (iframeBody as HTMLBodyElement).style.boxSizing = 'border-box';
          iframeBody.style.margin = '0 0';
          // iframeBody.style.border = `0 solid ${variables.colorV2.module.border.active}`;
          // iframeBody.style.borderWidth = '2px 0 2px 0';
          // iframeBody.style.borderRadius = '2px';
          iframeBody.style.overflow = 'hidden';
          // (iframeBody.children[0] as any).style.border = 'none';

          const contentTable = iframe?.contentDocument?.querySelectorAll('table')[1];
          const parentTable = iframe?.contentDocument.querySelectorAll('table')[0];

          const child = blockGrid?.children[0];

          const moduleContent = document.getElementById(MODULE_CONTENT_DIV_ID) as HTMLDivElement;

          const contentDocument = (document.getElementById(templateIframeId) as HTMLElementWithDocument)
            ?.contentDocument;
          const curModuleInTemplate = contentDocument?.getElementById(elemId);

          // set the iframe width
          if (moduleContent && curModuleInTemplate) {
            // const moduleWidth = addWidths(
            //   [ViewportWidth.Desktop, HIGHLIGHTED_ELEM_BORDER_WIDTH, HIGHLIGHTED_ELEM_BORDER_WIDTH],
            //   'px',
            // );
            let moduleWidth = ViewportWidth.Desktop;

            if (viewportSize === ViewportSize.Mobile) {
              // const moduleWidth = addWidths(
              //   [ViewportWidth.Mobile, HIGHLIGHTED_ELEM_BORDER_WIDTH, HIGHLIGHTED_ELEM_BORDER_WIDTH],
              //   'px',
              // );
              moduleWidth = ViewportWidth.Mobile;
            }

            moduleContent.style.minWidth = moduleWidth; // `${curModuleInTemplate?.getBoundingClientRect().width ?? 0}px`;
            moduleContent.style.width = moduleWidth; // `${curModuleInTemplate?.getBoundingClientRect().width ?? 0}px`;

            // iframe.style.height = `${iframe.contentDocument.body.getBoundingClientRect().height}px`;
            resizeModalIframe(iframe as HTMLIFrameElement, isTemplateEditor);
          } else if (blockGrid && child) {
            const childWidth = child.getBoundingClientRect().width;
            const curTemplateWidth = childWidth;

            if (moduleContent) {
              moduleContent.style.minWidth = `${curTemplateWidth + 10}px`;
            }

            return;
          } else if (
            !contentTable ||
            (contentTable.parentElement?.tagName === 'BODY' && parentTable?.getBoundingClientRect().height > 1)
          ) {
            const curTemplateWidth = parentTable?.getBoundingClientRect().width;

            if (moduleContent && curTemplateWidth) {
              moduleContent.style.minWidth = `${curTemplateWidth + 6}px`;
            }

            return;
          } else if (moduleContent) {
            // const curTemplateWidth = contentTable?.getBoundingClientRect().width;
            // moduleContent.style.minWidth = `${curTemplateWidth + 6}px`;

            // if displaying a module without a template, default to desktop width
            // const moduleWidth = addWidths(
            //   [ViewportWidth.Desktop, HIGHLIGHTED_ELEM_BORDER_WIDTH, HIGHLIGHTED_ELEM_BORDER_WIDTH],
            //   'px',
            // );
            const moduleWidth = `${ViewportWidth.Desktop}`;
            moduleContent.style.minWidth = moduleWidth; // `${curModuleInTemplate?.getBoundingClientRect().width ?? 0}px`;
            moduleContent.style.width = moduleWidth;

            // reset the iframe height
            // iframe.style.height = `${iframe.contentDocument.body.getBoundingClientRect().height}px`;
            resizeModalIframe(iframe as HTMLIFrameElement, isTemplateEditor);
          }
        }

        cb();

        // The following block of code never seems to run. Commenting out to determine impact
        // iframe.onload = () => {
        //   const iframeBodyNew = iframe.contentDocument.body;

        //   if (iframeBodyNew) {
        //     iframeBodyNew.style.margin = '0px';
        //     iframeBodyNew.style.border = `0.2rem solid ${variables.colorV2.module.border.active}`;
        //     iframeBodyNew.style.borderWidth = '0.2rem 0';
        //     iframeBodyNew.style.borderRadius = '0.2rem';
        //     (iframeBody.children[0] as any).style.border = 'none';
        //   }
        // };

        if (positionSelectedModuleIframeOverlay) {
          positionSelectedModuleIframeOverlay(selectedActiveModule);
        }
      };
    },
    [
      isTemplateEditor,
      positionSelectedModuleIframeOverlay,
      selectedActiveModule,
      setLeftPanelType,
      templateIframeId,
      templateStyles,
      viewportSize,
    ],
  );

  useEffect(() => {
    onIframeLoad(() => {
      const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;

      if (redefinedDoc || !redefinedDoc) {
        //const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;

        contentDocument.getElementById(chosenElemId)?.removeAttribute(MODULE_INTERACTION_DRAG_ATTRIBUTE);
        initializeElemIds(contentDocument, 'img');
        initializeElemIds(contentDocument, 'h1');
        initializeElemIds(contentDocument, 'h2');
        initializeElemIds(contentDocument, 'h3');
        initializeElemIds(contentDocument, 'h4');
        initializeElemIds(contentDocument, 'h5');
        initializeElemIds(contentDocument, 'h6');
        initializeElemIds(contentDocument, 'strong');
        initializeElemIds(contentDocument, 'span');
        initializeElemIds(contentDocument, 'p');
        // initializeElemIds(contentDocument, 'button');
        initializeLinkIds(contentDocument);
        initializeDivIds(contentDocument);

        // redefineElements();
        setChosenModuleElemId('a');
        // await delay(500);

        redefineHtml(redefinedDoc);
        attachIframeOverlays(document.getElementById(documentElemId) as HTMLIFrameElement);
        redefineElements();
        setChosenModuleElemId('');
      }
    }, chosenElemId);
  }, [
    redefinedDoc,
    redefineHtml,
    documentElemId,
    chosenElemId,
    // handleElemClick,
    // handleElemDoubleCLick,
    // handleElemMouseEnter,
    // handleElemMouseLeave,
    // handleSelectionChange,
    // chosenModuleElemId,
    onIframeLoad,
    redefineElements,
    setChosenModuleElemId,
  ]);

  useEffect(() => {
    wrapperRef.current = window.setTimeout(async () => {
      if (redefinedDoc) {
        // const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
        // await delay(300);
        // redefineElements();
        // await delay(500);
        // const elems = contentDocument.querySelectorAll('h1, p, img, strong, span');
        // elems.forEach((textElem: any) => {
        //   textElem.onmouseover = (e: MouseEvent) => handleElemMouseEnter(e, textElem.id);
        //   textElem.onmouseout = (e: MouseEvent) => handleElemMouseLeave(e, textElem.id);
        //   textElem.onclick = (e: MouseEvent) => handleElemClick(e);
        // });
        // const doubleClickableElems = contentDocument.querySelectorAll('h1, p, button, strong, span');
        // doubleClickableElems.forEach((textElem: any) => {
        //   textElem.ondblclick = (e: MouseEvent) => handleElemDoubleCLick(e, textElem.id, contentDocument);
        // });
        // const iframe = document.getElementById(documentElemId) as HTMLIFrameElement;
        // attachIframeOverlays(iframe);
        // redefineElements();
      }
    });

    return () => {
      window.clearTimeout(wrapperRef.current);
    };
  }, [
    redefinedDoc,
    // handleElemMouseEnter,
    // handleElemMouseLeave,
    // handleElemClick,
    // handleElemDoubleCLick,
    // redefineElemMethods,
    // redefineLinkElemMethods,
    // documentElemId,
    // chosenModuleElemId,
    // highlightedElems,
    // redefineElements,
    // redefineDivElemMethods,
  ]);

  const initRedefinedDocRef = useRef('');
  const prevRedefinedDocRef = useRef('');

  // useEffect(
  useLayoutEffect(
    () => {
      // const prevRedefinedDoc = prevRedefinedDocRef.current;

      // if (prevRedefinedDoc && prevRedefinedDoc !== redefinedDoc) {
      prevRedefinedDocRef.current = redefinedDoc;

      // #################################################
      // TODO: Test with the following code commented out. Does this break anything?
      // onChange(redefinedDoc, initRedefinedDocRef.current !== redefinedDoc, getClearModuleHtml);
      // }
    },
    //[onChange, getClearModuleHtml, redefinedDoc, documentElemId]);
    // [onChange, getClearModuleHtml, redefinedDoc],
    [redefinedDoc],
  );

  useLayoutEffect(() => {
    if (!initRedefinedDocRef.current && redefinedDoc) {
      initRedefinedDocRef.current = redefinedDoc;
      prevRedefinedDocRef.current = redefinedDoc;
    }
  }, [redefinedDoc]);

  return {
    redefinedDoc,
  };
};

// returns cleaned up HTML
export const getClearModuleHtmlExternally = (docElemId: string, chosenElemId: string) => {
  resetPrevElem('', docElemId, chosenElemId, func.nop); // clear selection
  // removeElemInfo('', docElemId); // clear hover

  const moduleFrame = document.getElementById(docElemId) as HTMLIFrameElement | null;

  // get all editable elements
  const editableTagNames = EDITABLE_ELEMENT_TAG_NAMES.join(',');
  const editableElems = moduleFrame?.contentDocument?.querySelectorAll(editableTagNames) ?? [];
  editableElems.forEach((elem: Element) => {
    // remove attribute originalinnerhtml
    if (elem.hasAttribute('originalinnerhtml')) {
      elem.removeAttribute('originalinnerhtml');
    }
    // if has original-id attribute, set as id attribute value and remove original-id attribute
    if (elem.hasAttribute(ORIGINAL_ID_ATTRIBUTE)) {
      const originalId = elem.getAttribute(ORIGINAL_ID_ATTRIBUTE);
      if (originalId) {
        elem.setAttribute('id', originalId);
      }
      elem.removeAttribute(ORIGINAL_ID_ATTRIBUTE);
    } else {
      elem.removeAttribute('id'); // remove id attribute if it doesn't have original-id
    }
  });

  // remove element info tags
  const redundantElems = Array.from(moduleFrame?.contentDocument?.querySelectorAll(`div`) ?? []).filter((item) =>
    item.id.includes('elem-info'),
  );
  redundantElems.forEach((elem) => elem.remove());

  // remove hover and active overlays
  if (moduleFrame) {
    removeIframeOverlays(moduleFrame);
  }

  // reset active elements hovered attribute
  const activeElems =
    moduleFrame?.contentDocument?.querySelectorAll(`[${MODULE_INTERACTION_ACTIVE_ATTRIBUTE}="true"]`) ?? [];
  activeElems.forEach((element: any) => {
    // element.style.borderColor = 'transparent';
    element.removeAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE);
  });

  // reset hovered elements hovered attribute
  const hoveredElems =
    moduleFrame?.contentDocument?.querySelectorAll(`[${MODULE_INTERACTION_HOVER_ATTRIBUTE}="true"]`) ?? [];
  hoveredElems.forEach((element: any) => {
    // element.style.borderColor = 'transparent';
    element.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
  });

  // reset elements with active border (this should no longer be needed since borders aren't added inline)
  const selectedElems = moduleFrame?.contentDocument?.querySelectorAll(
    `[style*="border-color:${variables.colorV2.module.border.active}"]`,
  );
  const selectedBorderElems = moduleFrame?.contentDocument?.querySelectorAll(
    `[style*="border: .2rem solid ${variables.colorV2.module.border.active}"]`,
  );
  const elems: any[] = Array.from(selectedElems ?? []).concat(Array.from(selectedBorderElems ?? []));

  elems.forEach((element: any) => {
    // element.style.borderColor = 'transparent';
    element.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
  });

  // remove absolute positioned div elements (may not be used)
  const redundantPrevElems = moduleFrame?.contentDocument?.querySelectorAll(`div[style*="position: absolute;"]`) ?? [];
  redundantPrevElems.forEach((elem) => elem.remove());

  const innerDoc = moduleFrame?.contentDocument || moduleFrame?.contentWindow?.document;
  const moduleElement = innerDoc?.querySelector(`[${DATA_SPLITTER_ATTRIBUTE}]`);

  // restore original structure of textareas using original-element attribute
  const textAreas = moduleFrame?.contentDocument?.querySelectorAll('textarea');
  textAreas?.forEach((textArea) => {
    const textAreaHtml = textArea.innerHTML;
    const parentElement = textArea.parentElement;

    if (parentElement) {
      const placeholder = document.createElement('div');
      placeholder.innerHTML = textArea.parentElement?.getAttribute('original-element') ?? '';
      const newNode = placeholder.firstElementChild;

      parentElement.replaceWith(newNode!);
      parentElement.innerHTML = textAreaHtml;
    }
  });

  // trigger blur event for all contenteditable elements
  const preElems = moduleFrame?.contentDocument?.querySelectorAll('[contenteditable="true"]') ?? [];
  preElems?.forEach((preElem: any) => {
    preElem.onblur({ target: preElem });
  });

  // return cleaned HTML and filled attribute value
  return {
    moduleHtml: moduleElement?.innerHTML || innerDoc?.body?.innerHTML || '',
    filledAttrValue: moduleElement?.getAttribute(DATA_FILLED_ATTRIBUTE) || '',
  };
};
